import {
	HttpClient,
	HttpErrorResponse,
	HttpHeaders,
} from '@angular/common/http';
import { DeploymentContext } from '../utilities/deployment-context/deployment-context.js';
import { Logger } from '../utilities/logger/logger.js';
import { catchError, firstValueFrom, Observable, of } from 'rxjs';

/** An object parameters structure to be adopted as Get/Post/Delete start to need more complex call signatures */
type RequestOptions<T> = {
	extraOptions?: any;
	handleError?: (error: HttpErrorResponse) => Observable<T>;
	/** This allows a caller with a complex GET query string to encodeURIComponent discrete elements */
	skipURIEncoding?: boolean;
};

export abstract class ServiceBase {
	protected _apiUrl: string;
	protected _unhostedApiUrl: string;
	protected _logger: Logger;
	protected _standardHeaders: HttpHeaders;
	protected defaultOptions;

	public constructor(
		protected _httpClient: HttpClient,
		protected _context: DeploymentContext,
		restApiRelativePath: string
	) {
		this._apiUrl = _context.buildApiUrl(restApiRelativePath);
		this._logger = new Logger(this.constructor.name);
		this._standardHeaders = _context.standardHttpHeaders;
		this.defaultOptions = {
			headers: this._standardHeaders,
		};
	}

	protected async get<T>(
		url: string,
		{
			extraOptions = {},
			handleError = (_error: HttpErrorResponse) => {
				return of({} as T);
			},
			skipURIEncoding = false,
		}: RequestOptions<T> = {}
	): Promise<T | null> {
		try {
			const encodedURL = skipURIEncoding ? url : encodeURI(url);
			const resp = (await firstValueFrom(
				this._httpClient
					.get<T>(encodedURL, {
						...this.defaultOptions,
						...extraOptions,
					})
					.pipe(
						catchError((error: HttpErrorResponse) => {
							return handleError(error);
						})
					)
			)) as T;
			return resp;
		} catch {
			// HTTP Errors are handled by the interceptor, so
			// no need to log or toast here
			return null;
		}
	}

	protected async post<T>(
		url: string,
		body: unknown = {},
		extraOptions = {}
	): Promise<T> {
		try {
			const resp = (await firstValueFrom(
				this._httpClient.post<T>(url, body, {
					...this.defaultOptions,
					...extraOptions,
				})
			)) as T;
			return resp;
		} catch {
			// HTTP Errors are handled by the interceptor, so
			// no need to log or toast here
			return null;
		}
	}

	protected async delete<T>(url: string, extraOptions = {}): Promise<T> {
		try {
			const resp = (await firstValueFrom(
				this._httpClient.delete<T>(url, {
					...this.defaultOptions,
					...extraOptions,
				})
			)) as T;
			return resp;
		} catch {
			// HTTP Errors are handled by the interceptor, so
			// no need to log or toast here
			return null;
		}
	}
}
